import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Calendar, Clock, ExternalLink, FileText } from 'lucide-react';

import { MessageBubble } from './MessageBubble';
import { DOCUMENT_CONSTANTS } from '../constants/document';
import { formatDocumentDate, getBoxUrl, isBoxNavigable } from '../utils/document';

import type { DocumentModalProps } from '../types/document';

export const DocumentModal: React.FC<DocumentModalProps> = ({
    isOpen,
    onOpenChange,
    documentData,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleGoToBox = () => {
        if (documentData) {
            const boxUrl = getBoxUrl(documentData.id);
            if (boxUrl) {
                navigate(boxUrl);
                onOpenChange(false); // Close modal after navigation
            }
        }
    };

    // Main modal implementation
    if (isOpen && documentData) {
        return (
            <div
                className={`fixed inset-0 ${DOCUMENT_CONSTANTS.MODAL_Z_INDEX} bg-black/50 flex items-center justify-center`}
                onClick={() => onOpenChange(false)}
            >
                <div
                    className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-4xl min-w-[800px] max-h-[80vh] overflow-y-auto m-4 shadow-xl"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                                <h2 className="text-xl font-semibold">
                                    {documentData?.title || t('ai.chatbot.reference_documents', '참조 문서')}
                                </h2>
                            </div>

                            {/* Document metadata */}
                            <div className="space-y-2 mb-3">
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        <span className="font-mono bg-muted/50 rounded px-1.5 py-0.5">
                                            {documentData?.id}
                                        </span>
                                    </div>
                                    {documentData?.externalType && (
                                        <div className="flex items-center gap-1">
                                            <span className="capitalize bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                                                {documentData.externalType}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {(documentData?.createdAt || documentData?.updatedAt || documentData?.meta$?.date) && (
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        {(documentData?.createdAt || documentData?.meta$?.date) && (
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                <span>
                                                    {documentData.meta$?.date ||
                                                        formatDocumentDate(documentData.createdAt)}
                                                </span>
                                            </div>
                                        )}
                                        {documentData?.updatedAt &&
                                            documentData.updatedAt !== documentData.createdAt && (
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    <span>수정됨 {formatDocumentDate(documentData.updatedAt)}</span>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center gap-2">
                                {documentData && isBoxNavigable(documentData.id) && (
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleGoToBox}
                                        className="flex items-center gap-1.5 text-xs h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        BOX에서 열기
                                    </Button>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => onOpenChange(false)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors flex-shrink-0 ml-4"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-start" role="group" aria-label="Document content">
                            <div className="relative group w-full text-title py-[10px] rounded-[18px]">
                                <div className="flex gap-3">
                                    <div className="flex-1 min-w-0">
                                        <MessageBubble
                                            isUser={false}
                                            messageId={`doc-${documentData.id}`}
                                            content={documentData.content}
                                            copyButtonContent={documentData.content}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state modal
    if (isOpen && isLoading) {
        return (
            <div
                className={`fixed inset-0 ${DOCUMENT_CONSTANTS.MODAL_Z_INDEX} bg-black/50 flex items-center justify-center`}
                onClick={() => onOpenChange(false)}
            >
                <div
                    className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-4xl min-w-[800px] max-h-[80vh] overflow-y-auto m-4 shadow-xl"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                                <h2 className="text-xl font-semibold">
                                    {t('ai.chatbot.reference_documents', '참조 문서')}
                                </h2>
                            </div>

                            {/* Loading metadata skeleton */}
                            <div className="space-y-2 mb-3">
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        <span className="font-mono bg-muted/50 rounded px-1.5 py-0.5">...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => onOpenChange(false)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors flex-shrink-0 ml-4"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="flex items-center gap-3 justify-center py-12">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        <span className="text-sm text-muted-foreground">문서를 불러오는 중...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('ai.chatbot.reference_documents', '참조 문서')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex items-center gap-2 justify-center py-8">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        <span className="text-sm text-muted-foreground">문서를 불러오는 중...</span>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
